import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  CloseButton,
  Modal,
  NavLink,
  Paper,
  SegmentedControl,
  Tooltip,
  createTheme,
  defaultVariantColorsResolver,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from '@mantine/core';

// Black ink on white paper, set in IBM Plex Sans, and nothing warmer than
// grey. The child's own mode is the one thing told apart by tone — charcoal
// for junior, slate for senior — so a nine-year-old still sees which is theirs
// before reading a label.
//
// The decisions live here, as component defaults, so a screen says `<Card>` or
// `<ActionIcon>` and gets the house look without a style prop. Icon buttons
// and close buttons are subtle, the active nav link is a grey wash, badges and
// alerts are light, and a Button left without a variant is the one filled,
// black action on its screen.

// A neutral grey with no tint. It replaces Mantine's own `gray` and `dark`, so
// `c="dimmed"`, every border and every disabled state lands on it without a
// call site asking.
const gray: MantineColorsTuple = [
  '#fafafa',
  '#f4f4f5',
  '#e4e4e7',
  '#d4d4d8',
  '#a1a1aa',
  '#71717a',
  '#52525b',
  '#3f3f46',
  '#27272a',
  '#18181b',
];

// The primary: black. Mantine fills at index 6 and hovers at 7, so the ink
// itself sits at 6 and the pale end is the wash a light variant is made from.
const ink: MantineColorsTuple = [
  '#f4f4f5',
  '#e4e4e7',
  '#d4d4d8',
  '#a1a1aa',
  '#71717a',
  '#3f3f46',
  '#18181b',
  '#09090b',
  '#09090b',
  '#000000',
];

// The two modes stay apart by colour, but neither is a colour in the loud
// sense: junior is charcoal and senior is slate, a grey with blue in it. Both
// sit on the same white and grey as everything else.
const junior: MantineColorsTuple = [
  '#fafafa',
  '#f4f4f5',
  '#e4e4e7',
  '#d4d4d8',
  '#a1a1aa',
  '#71717a',
  '#3f3f46',
  '#27272a',
  '#18181b',
  '#09090b',
];

const senior: MantineColorsTuple = [
  '#f8fafc',
  '#f1f5f9',
  '#e2e8f0',
  '#cbd5e1',
  '#94a3b8',
  '#64748b',
  '#475569',
  '#334155',
  '#1e293b',
  '#0f172a',
];

// A light badge or a subtle button writes its word in the colour it is washed
// in, and index 6 of a mid ramp on its own wash is under 4.5:1. The word moves
// two steps down its ramp; the wash, hover and border stay Mantine's.
const READABLE: Record<string, string> = {
  junior: 'var(--mantine-color-junior-8)',
  senior: 'var(--mantine-color-senior-8)',
  orange: 'var(--mantine-color-orange-9)',
  yellow: 'var(--mantine-color-yellow-9)',
  green: 'var(--mantine-color-green-8)',
  gray: 'var(--mantine-color-gray-7)',
  ink: 'var(--mantine-color-ink-6)',
};

const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  const writes = input.variant === 'light' || input.variant === 'subtle';
  const text = writes && input.color ? READABLE[input.color] : undefined;
  return text ? { ...resolved, color: text } : resolved;
};

// The page is off-white so a white card reads as a sheet on it.
const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: { '--mantine-color-body': '#fafafa' },
  dark: {},
});

const PLEX = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const theme = createTheme({
  colors: { gray, dark: gray, ink, junior, senior },
  primaryColor: 'ink',
  primaryShade: 6,
  white: '#ffffff',
  black: '#18181b',
  variantColorResolver,

  fontFamily: PLEX,
  fontFamilyMonospace: '"IBM Plex Mono", ui-monospace, Menlo, monospace',
  headings: {
    fontFamily: PLEX,
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2.25rem', lineHeight: '1.15' },
      h2: { fontSize: '1.75rem', lineHeight: '1.2' },
      h3: { fontSize: '1.375rem', lineHeight: '1.25' },
      h4: { fontSize: '1.125rem', lineHeight: '1.3' },
      h5: { fontSize: '1rem', lineHeight: '1.35' },
      h6: { fontSize: '0.875rem', lineHeight: '1.4' },
    },
  },

  defaultRadius: 'md',
  radius: { xs: '4px', sm: '6px', md: '10px', lg: '14px', xl: '20px' },
  cursorType: 'pointer',
  focusRing: 'auto',

  // Hairlines do the separating; a shadow is kept for what floats — a modal,
  // a menu, a toast.
  shadows: {
    xs: '0 1px 2px rgba(24,24,27,.04)',
    sm: '0 1px 2px rgba(24,24,27,.05), 0 1px 3px rgba(24,24,27,.04)',
    md: '0 4px 12px -2px rgba(24,24,27,.08), 0 2px 4px rgba(24,24,27,.04)',
    lg: '0 12px 32px -8px rgba(24,24,27,.16)',
    xl: '0 24px 48px -12px rgba(24,24,27,.22)',
  },

  components: {
    ActionIcon: ActionIcon.extend({
      defaultProps: { variant: 'subtle', color: 'gray' },
    }),
    CloseButton: CloseButton.extend({
      defaultProps: { variant: 'subtle' },
    }),
    Button: Button.extend({
      defaultProps: { fw: 500 },
    }),
    Badge: Badge.extend({
      defaultProps: { variant: 'light', color: 'gray', radius: 'sm', tt: 'none', fw: 500 },
    }),
    Alert: Alert.extend({
      defaultProps: { variant: 'light', color: 'gray', radius: 'md' },
    }),
    Card: Card.extend({
      defaultProps: { withBorder: true, radius: 'lg', shadow: 'xs' },
    }),
    Paper: Paper.extend({
      defaultProps: { radius: 'lg' },
    }),
    Modal: Modal.extend({
      defaultProps: { radius: 'lg', shadow: 'xl', overlayProps: { backgroundOpacity: 0.35, blur: 2 } },
    }),
    NavLink: NavLink.extend({
      defaultProps: { variant: 'light', color: 'ink' },
      styles: { root: { borderRadius: 'var(--mantine-radius-md)' }, label: { fontWeight: 500 } },
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: { radius: 'md' },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: { color: 'ink', radius: 'sm', openDelay: 300 },
    }),
  },
});

export { cssVariablesResolver };

/** How a mode looks. Junior is charcoal and senior is slate; `soft` is the
 * page a child's screen sits on, the same neutral grey for both. */
export function accent(mode: string): { color: string; hex: string; soft: string; label: string } {
  return mode === 'junior'
    ? { color: 'junior', hex: junior[6], soft: '#fafafa', label: 'Junior' }
    : { color: 'senior', hex: senior[6], soft: '#fafafa', label: 'Senior' };
}
